import { createContext, ReactNode, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { immer } from "zustand/middleware/immer"
import { AiTrackingMetadata } from "@/modules/chats/types/chat-message"

type AiRunningState =
  | "NONE"
  | "CHAT_SUMMARIZER"
  | "NEXT_BEST_QUESTIONS"
  | "QUESTIONNAIRE_SUMMARIZER"
  | "CHAT_CASE_SUMMARY_CONFIRMATION"
  | "CALL_TRANSCRIPT_SUMMARIZER"
  | "TRANSLATE"
  | "MAGIC_WAND"

interface ChatInputState {
  aiRunning: AiRunningState
  aiTrackingMetadata: AiTrackingMetadata[] | null
  messageText: string
  nextBestQuestions: string[]
  isSendingMessage: boolean
  setAiRunning: (aiRunning: AiRunningState) => void
  setAiTrackingMetadata: (aiTrackingMetadata: AiTrackingMetadata[] | null) => void
  setMessageText: (messageText: string) => void
  setNextBestQuestions: (nextBestQuestions: string[]) => void
  setIsSendingMessage: (isSendingMessage: boolean) => void
  resetAiRunning: VoidFunction
}

const createChatInputStore = () => {
  return create<ChatInputState>()(
    immer((set, get) => ({
      aiRunning: "NONE",
      aiTrackingMetadata: null,
      messageText: "",
      nextBestQuestions: [],
      isSendingMessage: false,
      setAiRunning: (aiRunning) => set({ aiRunning }),
      setAiTrackingMetadata: (aiTrackingMetadata) => {
        if (aiTrackingMetadata) {
          set({ aiTrackingMetadata: [...(get().aiTrackingMetadata ?? []), ...aiTrackingMetadata] })
        } else {
          set({ aiTrackingMetadata })
        }
      },
      setMessageText: (messageText) => set({ messageText }),
      setNextBestQuestions: (nextBestQuestions) => set({ nextBestQuestions }),
      setIsSendingMessage: (isSendingMessage) => set({ isSendingMessage }),
      resetAiRunning: () => set({ aiRunning: "NONE" }),
    })),
  )
}

type ChatInputStore = ReturnType<typeof createChatInputStore>

const ChatInputStoreContext = createContext<ChatInputStore | null>(null)

export const ChatInputProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<ChatInputStore>(createChatInputStore())

  return <ChatInputStoreContext.Provider value={storeRef.current}>{children}</ChatInputStoreContext.Provider>
}

export function useChatInputContext<T>(selector: (state: ChatInputState) => T): T {
  const store = useContext(ChatInputStoreContext)
  if (!store) throw new Error("Missing ChatInputStoreContext.Provider in the tree")

  return useStore(store, selector)
}

/**
 * Non-reactive read access to chat-input state — does NOT subscribe, so it never re-renders the caller.
 * Returns a getter to call inside handlers/effects, e.g. `const { messageText } = getChatInputState()`.
 * Use `useChatInputContext(selector)` instead for values that are rendered in JSX.
 */
export function useChatInputStateGetter(): () => ChatInputState {
  const store = useContext(ChatInputStoreContext)
  if (!store) throw new Error("Missing ChatInputStoreContext.Provider in the tree")

  return store.getState
}
